import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Plus } from 'lucide-react';

const balanceText = (balanceLoading, workerBalance, fmt) => {
  if (balanceLoading) return 'Loading balance...';
  if (workerBalance) return `Net balance owed: ${fmt(workerBalance.net_balance)}`;
  return 'Balance unavailable';
};

export const PayoutFormDialog = ({
  open,
  onOpenChange,
  workers,
  formData,
  setFormData,
  onSubmit,
  isSubmitting,
  symbol,
  fmt,
  workerBalance,
  balanceLoading
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button
        data-testid="add-payout-btn"
        className="h-12 px-6 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
      >
        <Plus className="w-5 h-5 mr-2" />
        Record Payout
      </Button>
    </DialogTrigger>
    <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl font-semibold text-[#1C1C1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Record Wage Payout
        </DialogTitle>
        <DialogDescription className="text-[#5C5C5C]">
          Log a wage payment made to a worker against their accumulated balance.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label>Worker *</Label>
          <Select
            value={formData.worker_id}
            onValueChange={(value) => setFormData({ ...formData, worker_id: value })}
          >
            <SelectTrigger data-testid="payout-worker-select" className="h-12 border-[#D1D1CD]">
              <SelectValue placeholder="Select worker" />
            </SelectTrigger>
            <SelectContent>
              {workers.map(worker => (
                <SelectItem key={worker.id} value={worker.id}>
                  {worker.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.worker_id && (
            <div
              data-testid="worker-net-balance"
              className={`text-sm rounded-md px-3 py-2 border ${
                workerBalance && workerBalance.net_balance < 0
                  ? 'bg-[#BC4749]/10 border-[#BC4749]/30 text-[#BC4749]'
                  : 'bg-[#2E4632]/5 border-[#2E4632]/20 text-[#2E4632]'
              }`}
            >
              {balanceText(balanceLoading, workerBalance, fmt)}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Amount ({symbol}) *</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] font-medium">
              {symbol}
            </span>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              data-testid="payout-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="Enter amount"
              className="pl-10 h-12 border-[#D1D1CD]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Payment Date *</Label>
          <Input
            type="date"
            data-testid="payout-date-input"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="h-12 border-[#D1D1CD]"
          />
        </div>

        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            data-testid="payout-notes-input"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optional note (e.g. paid via bank transfer)"
            className="border-[#D1D1CD]"
            rows={3}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-12 border-[#D1D1CD]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="payout-submit-btn"
            disabled={isSubmitting}
            className="flex-1 h-12 bg-[#2E4632] hover:bg-[#1F3122] text-white"
          >
            {isSubmitting ? 'Recording...' : 'Record Payout'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
